import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { Bell, Mail, MessageCircle, Clock, Calendar, Loader2, Settings2 } from 'lucide-react';

interface NotificationPrefs {
  deadline_notifications_app: boolean;
  deadline_notifications_email: boolean;
  deadline_notifications_telegram: boolean;
  telegram_chat_id: string | null;
  default_reminder_days: number[];
  notification_hour: number;
}

const REMINDER_DAY_OPTIONS = [
  { value: 30, label: '30d' },
  { value: 15, label: '15d' },
  { value: 7, label: '7d' },
  { value: 5, label: '5d' },
  { value: 3, label: '3d' },
  { value: 2, label: '2d' },
  { value: 1, label: '1d' },
  { value: 0, label: 'Hoy' },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${i.toString().padStart(2, '0')}:00`,
}));

export function CalendarNotificationSettings() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingDays, setPendingDays] = useState<number[]>([]);
  const [hasDayChanges, setHasDayChanges] = useState(false);

  const loadPrefs = useCallback(async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('deadline_notifications_app, deadline_notifications_email, deadline_notifications_telegram, telegram_chat_id, default_reminder_days, notification_hour')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      const p: NotificationPrefs = {
        deadline_notifications_app: data.deadline_notifications_app ?? true,
        deadline_notifications_email: data.deadline_notifications_email ?? true,
        deadline_notifications_telegram: data.deadline_notifications_telegram ?? false,
        telegram_chat_id: data.telegram_chat_id,
        default_reminder_days: (data as any).default_reminder_days ?? [7, 3, 1, 0],
        notification_hour: (data as any).notification_hour ?? 8,
      };
      setPrefs(p);
      setPendingDays(p.default_reminder_days);
    } catch (e) {
      console.error('Error loading notification prefs:', e);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => { loadPrefs(); }, [loadPrefs]);

  const updatePref = async (key: keyof NotificationPrefs, value: any) => {
    if (!user || !prefs) return;
    setIsSaving(true);
    const prev = prefs[key];
    setPrefs(p => p ? { ...p, [key]: value } : null);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ [key]: value })
        .eq('id', user.id);
      if (error) throw error;
      toast.success('Configuración actualizada');
    } catch (e) {
      console.error('Error updating pref:', e);
      setPrefs(p => p ? { ...p, [key]: prev } : null);
      toast.error('Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleDay = (day: number) => {
    const newDays = pendingDays.includes(day)
      ? pendingDays.filter(d => d !== day)
      : [...pendingDays, day].sort((a, b) => b - a);
    setPendingDays(newDays);
    setHasDayChanges(true);
  };

  const saveDays = async () => {
    if (pendingDays.length === 0) {
      toast.error('Selecciona al menos un día');
      return;
    }
    await updatePref('default_reminder_days', pendingDays);
    setHasDayChanges(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!prefs) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Settings2 className="h-5 w-5 text-muted-foreground" />
        <h3 className="font-medium">Configuración de avisos</h3>
      </div>

      {/* Canales */}
      <Card>
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Canales de notificación
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-3.5 w-3.5 text-primary" />
              <Label htmlFor="cal-app" className="text-sm">In-app</Label>
            </div>
            <Switch
              id="cal-app"
              checked={prefs.deadline_notifications_app}
              onCheckedChange={(v) => updatePref('deadline_notifications_app', v)}
              disabled={isSaving}
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="h-3.5 w-3.5 text-blue-500" />
              <Label htmlFor="cal-email" className="text-sm">Email</Label>
            </div>
            <Switch
              id="cal-email"
              checked={prefs.deadline_notifications_email}
              onCheckedChange={(v) => updatePref('deadline_notifications_email', v)}
              disabled={isSaving}
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-3.5 w-3.5 text-sky-500" />
              <Label htmlFor="cal-telegram" className="text-sm">Telegram</Label>
              {prefs.telegram_chat_id ? (
                <Badge variant="secondary" className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                  Vinculado
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px]">No vinculado</Badge>
              )}
            </div>
            <Switch
              id="cal-telegram"
              checked={prefs.deadline_notifications_telegram}
              onCheckedChange={(v) => updatePref('deadline_notifications_telegram', v)}
              disabled={isSaving || !prefs.telegram_chat_id}
            />
          </div>
        </CardContent>
      </Card>

      {/* Días de recordatorio */}
      <Card>
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Días de recordatorio
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {REMINDER_DAY_OPTIONS.map((opt) => (
              <div
                key={opt.value}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border cursor-pointer transition-colors text-xs ${
                  pendingDays.includes(opt.value)
                    ? 'bg-primary/10 border-primary text-primary font-medium'
                    : 'bg-muted/30 border-transparent hover:border-muted-foreground/20'
                }`}
                onClick={() => toggleDay(opt.value)}
              >
                <Checkbox
                  checked={pendingDays.includes(opt.value)}
                  onCheckedChange={() => toggleDay(opt.value)}
                  className="h-3 w-3"
                />
                {opt.label}
              </div>
            ))}
          </div>

          {hasDayChanges && (
            <div className="flex justify-end">
              <Button onClick={saveDays} disabled={isSaving || pendingDays.length === 0} size="sm" className="text-xs h-7">
                {isSaving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Guardar
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hora */}
      <Card>
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Hora de notificación
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <Select
            value={prefs.notification_hour.toString()}
            onValueChange={(v) => updatePref('notification_hour', parseInt(v, 10))}
            disabled={isSaving}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HOUR_OPTIONS.map((h) => (
                <SelectItem key={h.value} value={h.value.toString()}>
                  {h.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground mt-1.5">
            Hora de España para email y Telegram
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
